/**
 * Platform billing / revenue query hook (super-admin only).
 *
 * The smart data layer behind the `/platform-billing` operator page. Unlike the
 * tenant credit hooks (`useCredits.ts`, credits-only), this returns REAL MONEY —
 * COGS, revenue and profit — because this is the owner surface, guarded by the
 * super-admin check on the route (`requireAdmin`) and independently by the
 * backend. It is NOT tenant-scoped, so — like the other `queryKeys.admin.*`
 * keys — it takes no org id; the `from`/`to` window is the whole cache identity.
 */

import { type UseQueryResult, useQuery } from "@tanstack/react-query"
import { fetchPlatformBilling, type IPlatformOverview } from "../lib/api/platform-billing.ts"
import { queryKeys } from "../lib/query-keys.ts"

/**
 * Fetch the platform billing overview for an inclusive `from`/`to` window. The
 * money figures are scoped to the window; the credit-wallet figures are lifetime.
 */
export function usePlatformBilling({ from, to }: { from: string; to: string }): UseQueryResult<IPlatformOverview> {
  return useQuery({
    queryKey: queryKeys.admin.platformBilling(from, to),
    queryFn: () => fetchPlatformBilling(from, to),
  })
}
